import { apiGet, apiPost } from './api-client';
import { apiV1Path, xatSmilieImageUrl } from './api-paths';
import { normalizeSmileySizePx } from '@/lib/xat-smilie';
import type { ApiPayload } from '@/components/help-center/latest-power-shared';

const XATBLOG_LATEST = 'https://api.xatblog.net/latest';
const XATBLOG_COUNTDOWN = 'https://api.xatblog.net/countdown';
const XATBLOG_POWERLOGS = 'https://api.xatblog.net/powerlogs';
const XATUSER_URL = 'https://xatproject.com/winterfestival-api/xatuser.php';
const XATBLOG_REG2ID = 'https://api.xatblog.net/reg2id';

const JSON_HEADERS = { Accept: 'application/json' };

export type XatProfileResponse = {
  userId?: string | null;
  avatarUrl?: string | null;
  regname?: string | null;
  error?: string;
};

async function fetchXatProxyJson(apiPath: string, remoteUrl: string): Promise<unknown | null> {
  try {
    const res = await fetch(apiV1Path(apiPath), { cache: 'no-store', headers: JSON_HEADERS });
    if (res.ok) return await res.json();
  } catch {
    /* backend indisponível */
  }

  try {
    const res = await fetch(remoteUrl, { cache: 'no-store', headers: JSON_HEADERS });
    if (res.ok) return await res.json();
  } catch {
    /* rede / CORS */
  }

  return null;
}

export async function getPowerInfo(power: string): Promise<unknown> {
  return apiGet(`/xat/powerinfo/${encodeURIComponent(power)}`);
}

export async function getStickerList(power: string): Promise<{
  code?: number;
  power?: {
    id?: number;
    name?: string;
    sticker_list?: string[];
  };
} | null> {
  const slug = power.trim().toLowerCase();
  if (!slug) return null;

  try {
    const data = await apiGet(`/xat/stickerlist/${encodeURIComponent(slug)}`);
    return data as { code?: number; power?: { id?: number; name?: string; sticker_list?: string[] } };
  } catch {
    /* backend down — tenta API pública */
  }

  try {
    const res = await fetch(`https://api.xatblog.net/stickerlist/${encodeURIComponent(slug)}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    return (await res.json()) as {
      code?: number;
      power?: { id?: number; name?: string; sticker_list?: string[] };
    };
  } catch {
    return null;
  }
}

export async function getPowerLogs(power: string): Promise<Record<string, unknown>> {
  const key = power.trim().toLowerCase();
  if (!key) throw new Error('Power inválido');

  const data = await fetchXatProxyJson(
    `/xat/powerlogs/${encodeURIComponent(key)}`,
    `${XATBLOG_POWERLOGS}/${encodeURIComponent(key)}`
  );

  if (data && typeof data === 'object') return data as Record<string, unknown>;
  throw new Error('Histórico indisponível');
}

export async function getFairtradePrices(): Promise<unknown> {
  return apiGet('/xat/fairtrade-prices');
}

/** Catálogo agregado de powers (api.xatblog.net/prices via proxy). */
export async function getPowersPricesCatalog(): Promise<unknown> {
  return apiGet('/xat/prices');
}

/** Presença online via proxy interno (não expõe api.xatblog.net). */
export async function getOnlineCheck(id: string): Promise<{
  code?: number;
  id?: string;
  status?: string;
  online?: boolean;
}> {
  return apiGet(`/xat/onlinecheck/${encodeURIComponent(id)}`);
}

export async function getOnlineCheckBatch(ids: string[]): Promise<{
  code?: number;
  statuses?: Record<string, { status: string; online: boolean }>;
}> {
  const qs = encodeURIComponent(ids.join(','));
  return apiGet(`/xat/onlinecheck?ids=${qs}`);
}

export async function getCollectionList(): Promise<unknown> {
  return apiGet('/xat/collectionlist');
}

export async function getAllpowersRequired(): Promise<unknown> {
  return apiGet('/xat/allpowers-required');
}

export async function getLatestPower(): Promise<ApiPayload> {
  const data = await fetchXatProxyJson('/xat/latest-power', XATBLOG_LATEST);
  if (data && typeof data === 'object' && Array.isArray((data as ApiPayload).powers)) {
    return data as ApiPayload;
  }
  return {
    code: 503,
    powers: [],
    status: 'Indisponível',
  } as ApiPayload;
}

export async function getCountdown(): Promise<unknown> {
  const data = await fetchXatProxyJson('/xat/countdown', XATBLOG_COUNTDOWN);
  if (data) return data;
  return {
    code: 503,
    status: 'Indisponível',
    status_code: 'no_countdown',
    power_info: null,
  };
}

async function fetchXatProfileFromSource(params: {
  id?: string;
  regname?: string;
}): Promise<XatProfileResponse> {
  let id = params.id?.trim() ?? '';
  const regnameRaw = params.regname?.trim() ?? '';

  if ((!id || !/^\d+$/.test(id)) && regnameRaw) {
    try {
      const r2 = await fetch(`${XATBLOG_REG2ID}/${encodeURIComponent(regnameRaw)}`, {
        headers: JSON_HEADERS,
      });
      if (!r2.ok) return { avatarUrl: null, userId: null, error: 'reg2id_upstream' };
      const regData = (await r2.json()) as { code?: number; xatid?: number };
      if (regData.code !== 200 || regData.xatid == null) {
        return { avatarUrl: null, userId: null, error: 'regname_not_found' };
      }
      id = String(regData.xatid);
    } catch {
      return { avatarUrl: null, userId: null, error: 'reg2id_fetch' };
    }
  }

  if (!id || !/^\d+$/.test(id)) {
    return { avatarUrl: null, userId: null, error: 'missing_or_invalid_id' };
  }

  try {
    const url = `${XATUSER_URL}?${new URLSearchParams({ id })}`;
    const res = await fetch(url, { headers: JSON_HEADERS });
    if (!res.ok) return { avatarUrl: null, userId: id, error: 'upstream' };

    const data = (await res.json()) as {
      success?: boolean;
      profile?: { avatar?: string; name?: string };
    };

    if (!data.success || !data.profile) {
      return { avatarUrl: null, userId: id, regname: regnameRaw || null, error: 'not_found' };
    }

    const avatar =
      typeof data.profile.avatar === 'string' ? data.profile.avatar.trim() : '';
    const resolvedRegname =
      typeof data.profile.name === 'string' && data.profile.name.trim()
        ? data.profile.name.trim()
        : regnameRaw || null;

    return {
      userId: id,
      avatarUrl: avatar.length > 0 ? avatar : null,
      regname: resolvedRegname,
    };
  } catch {
    return { avatarUrl: null, userId: id, error: 'fetch' };
  }
}

export async function getXatProfile(params: {
  id?: string;
  regname?: string;
}): Promise<XatProfileResponse> {
  const qs = new URLSearchParams();
  if (params.id) qs.set('id', params.id);
  if (params.regname) qs.set('regname', params.regname);

  try {
    const res = await fetch(apiV1Path(`/xat/xat-profile?${qs}`), {
      cache: 'no-store',
      headers: JSON_HEADERS,
    });
    if (res.ok) {
      const data = (await res.json()) as XatProfileResponse;
      if (data.avatarUrl || data.userId) return data;
    }
  } catch {
    /* backend indisponível */
  }

  return fetchXatProfileFromSource(params);
}

export async function validateSmilie(code: string): Promise<unknown> {
  return apiGet(`/xat/smilie/validate?code=${encodeURIComponent(code)}`);
}

export function getSmilieImageUrl(code: string, size = 40): string {
  return xatSmilieImageUrl({ name: code, size: String(normalizeSmileySizePx(size)) });
}

export async function uploadAceFrame(file: File, frameId: string): Promise<unknown> {
  const form = new FormData();
  form.append('file', file);
  form.append('frameId', frameId);
  return apiPost('/xat/ace-frame/upload', form);
}

export function getAceFrameImageUrl(frameId: string): string {
  return `/api/v1/xat/ace-frame/${encodeURIComponent(frameId)}/image`;
}
